import NumberInput from "../../../components/NumberInput";
import { formatNumberInput } from "../../../utils/helpers/numberInput";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import AkasSelect from "../../../components/AkasSelect";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import UserAvatar from "../../../components/UserAvatar";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import {
  getBaseUnitValue,
  getPriceUnitLabel,
  getUnitFactorToBase,
} from "../../../utils/helpers/pricing";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import "./style.css";

const emptyItem = () => ({ material_id: "", quantity: "", unit: "" });

const formulaSelectStyles = {
  menu: (base) => ({ ...base, zIndex: 1095 }),
  menuPortal: (base) => ({ ...base, zIndex: 1095 }),
};

const numberValue = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const MaterialFormulaModal = ({
  material,
  materials = [],
  units = [],
  formula,
  loading,
  error,
  fetchFormula,
  saveFormula,
  removeFormula,
}) => {
  const bodyRef = useRef(null);
  const unitOptions = useMemo(() => toUnitOptions(units), [units]);
  const [form, setForm] = useState({
    process_name: "",
    output_quantity: "",
    output_unit: "",
    process_cost: "",
    items: [emptyItem()],
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [itemErrors, setItemErrors] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const defaultOutputUnit = useMemo(
    () => getBaseUnitValue(unitOptions, material?.unit || "pcs"),
    [material?.unit, unitOptions],
  );

  useEffect(() => {
    if (!material?.id) return;

    let active = true;
    setIsReady(false);
    setValidationErrors({});
    setItemErrors([]);
    setForm({
      process_name: "",
      output_quantity: ["gram", "ml"].includes(defaultOutputUnit) ? "1000" : "1",
      output_unit: defaultOutputUnit,
      process_cost: "",
      items: [emptyItem()],
    });

    fetchFormula(material.id)
      .then((data) => {
        if (!active || !data) return;
        setForm({
          process_name: data.process_name || "",
          output_quantity: data.output_quantity ?? "",
          output_unit: data.output_unit || defaultOutputUnit,
          process_cost: data.process_cost || "",
          items: data.items?.length
            ? data.items.map((item) => ({
                material_id: item.material_id,
                quantity: item.quantity,
                unit: item.unit,
              }))
            : [emptyItem()],
        });
      })
      .finally(() => {
        if (active) setIsReady(true);
      });

    return () => {
      active = false;
    };
  }, [defaultOutputUnit, fetchFormula, material?.id]);

  const compatibleUnitOptions = (unit) => {
    const baseUnit = getBaseUnitValue(unitOptions, unit);
    return unitOptions.filter((option) => getBaseUnitValue(unitOptions, option.value) === baseUnit);
  };

  const materialOptions = useMemo(
    () =>
      materials
        .filter((item) => String(item.id) !== String(material?.id || ""))
        .map((item) => ({
          value: item.id,
          label: item.name,
          material: item,
        })),
    [material?.id, materials],
  );

  const selectedMaterial = (materialId) =>
    materials.find((item) => String(item.id) === String(materialId || ""));

  const updateItem = (index, values) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...values } : item)),
    }));
    setItemErrors((current) => current.map((item, itemIndex) => (itemIndex === index ? {} : item)));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.length === 1 ? [emptyItem()] : current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
    setItemErrors([]);
  };

  const estimate = useMemo(() => {
    const ingredientCost = form.items.reduce((total, item) => {
      const source = materials.find((materialItem) => String(materialItem.id) === String(item.material_id || ""));
      if (!source) return total;

      const baseUnit = getBaseUnitValue(unitOptions, item.unit || source.unit);
      const baseQuantity = numberValue(item.quantity) * getUnitFactorToBase(unitOptions, item.unit || source.unit);
      const unitCost = numberValue(source.price_trend?.current_price || source.unit_cost);
      const cost = ["gram", "ml"].includes(baseUnit)
        ? unitCost * (baseQuantity / 1000)
        : unitCost * baseQuantity;

      return total + cost;
    }, 0);
    const batchCost = ingredientCost + numberValue(form.process_cost);
    const outputBase = numberValue(form.output_quantity) * getUnitFactorToBase(unitOptions, form.output_unit);
    const outputRoot = getBaseUnitValue(unitOptions, form.output_unit);
    const priceBase = ["gram", "ml"].includes(outputRoot) ? 1000 : 1;

    return {
      batchCost,
      effectiveUnitCost: outputBase > 0 ? (batchCost / outputBase) * priceBase : 0,
      priceLabel: getPriceUnitLabel(unitOptions, form.output_unit),
    };
  }, [form.items, form.output_quantity, form.output_unit, form.process_cost, materials, unitOptions]);

  const validate = () => {
    const nextErrors = {};
    const nextItemErrors = form.items.map((item) => {
      const rowErrors = {};
      if (!item.material_id) rowErrors.material_id = "Pilih material penyusun.";
      if (numberValue(item.quantity) <= 0) rowErrors.quantity = "Takaran harus lebih dari 0.";
      if (!item.unit) rowErrors.unit = "Pilih satuan.";
      return rowErrors;
    });
    const selectedIds = form.items.map((item) => String(item.material_id || "")).filter(Boolean);
    if (new Set(selectedIds).size !== selectedIds.length) {
      form.items.forEach((item, index) => {
        if (item.material_id && selectedIds.filter((id) => id === String(item.material_id)).length > 1) {
          nextItemErrors[index].material_id = "Material ini sudah dipilih pada baris lain.";
        }
      });
    }
    if (!form.process_name.trim()) nextErrors.process_name = "Nama proses wajib diisi.";
    if (numberValue(form.output_quantity) <= 0) nextErrors.output_quantity = "Jumlah hasil harus lebih dari 0.";
    if (!form.output_unit) nextErrors.output_unit = "Satuan hasil wajib dipilih.";
    if (numberValue(form.process_cost) < 0) nextErrors.process_cost = "Biaya proses tidak boleh negatif.";

    setValidationErrors(nextErrors);
    setItemErrors(nextItemErrors);
    const hasErrors = Object.keys(nextErrors).length || nextItemErrors.some((row) => Object.keys(row).length);
    if (hasErrors) {
      window.requestAnimationFrame(() => bodyRef.current?.querySelector(".is-invalid")?.focus());
    }
    return !hasErrors;
  };

  const handleSave = async () => {
    if (!validate() || loading) return;
    const success = await saveFormula(material.id, {
      process_name: form.process_name.trim(),
      output_quantity: numberValue(form.output_quantity),
      output_unit: form.output_unit,
      process_cost: Math.round(numberValue(form.process_cost)),
      items: form.items.map((item) => ({
        material_id: Number(item.material_id),
        quantity: numberValue(item.quantity),
        unit: item.unit,
      })),
    });
    if (success) document.querySelector("#modal-material-formula .btn-close")?.click();
  };

  const handleDisable = async () => {
    if (!material?.id) return false;
    const success = await removeFormula(material.id);
    if (success) document.querySelector("#modal-material-formula .btn-close")?.click();
    return success;
  };

  const formulaExists = Boolean(formula?.id && String(formula.material_id) === String(material?.id));

  return (
    <>
      <div
        className="modal modal-blur fade material-formula-modal"
        id="modal-material-formula"
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(4px)" }}>
        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center min-w-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-2" aria-hidden="true">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                  <path d="M8 3v6M16 9v6M10 15v6" />
                </svg>
                <span className="text-truncate">Formula Material {material?.name || ""}</span>
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Tutup"></button>
            </div>

            <div ref={bodyRef} className="modal-body material-formula-modal__body">
              {!isReady ? (
                <div className="material-formula-loading" aria-live="polite">
                  <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                  <span>Memuat formula…</span>
                </div>
              ) : (
                <>
                  {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}

                  <div className="alert alert-primary d-flex align-items-start gap-3 mb-3" role="status">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="flex-shrink-0 mt-1" aria-hidden="true">
                      <path d="M12 3v6m0 6v6M5 7l7 2l7-2M5 17l7-2l7 2" />
                      <path d="M5 7v10m14-10v10" />
                    </svg>
                    <div>
                      <div className="fw-bold">Material virtual, tanpa proses produksi</div>
                      <div className="small">
                        Stok dan HPP dihitung dari bahan penyusun. Formula bertingkat tetap ditelusuri sampai bahan dasar.
                      </div>
                    </div>
                    <span className={`badge ms-auto flex-shrink-0 ${formulaExists ? "bg-primary text-primary-fg" : "bg-secondary-lt text-secondary"}`}>
                      {formulaExists ? "Formula aktif" : "Belum diatur"}
                    </span>
                  </div>

                  <div className="row g-3">
                    <div className="col-lg-6">
                      <label className="form-label" htmlFor="formula-process-name">Nama proses</label>
                      <input
                        id="formula-process-name"
                        type="text"
                        maxLength="100"
                        className={`form-control ${validationErrors.process_name ? "is-invalid" : ""}`}
                        value={form.process_name}
                        placeholder="Contoh: Roasting atau Racik Sambal"
                        aria-invalid={Boolean(validationErrors.process_name)}
                        onChange={(event) => {
                          setForm((current) => ({ ...current, process_name: event.target.value }));
                          setValidationErrors((current) => ({ ...current, process_name: "" }));
                        }}
                      />
                      {validationErrors.process_name ? <div className="invalid-feedback">{validationErrors.process_name}</div> : null}
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <label className="form-label" htmlFor="formula-output">Hasil formula</label>
                      <div className="input-group">
                        <NumberInput
                          id="formula-output"
                          type="number"
                          min="0.0001"
                          step="any"
                          className={`form-control ${validationErrors.output_quantity ? "is-invalid" : ""}`}
                          value={form.output_quantity}
                          aria-invalid={Boolean(validationErrors.output_quantity)}
                          onWheel={(event) => event.currentTarget.blur()}
                          onChange={(event) => {
                            setForm((current) => ({ ...current, output_quantity: event.target.value }));
                            setValidationErrors((current) => ({ ...current, output_quantity: "" }));
                          }}
                        />
                        <AkasSelect
                          className={`form-select ${validationErrors.output_unit ? "is-invalid" : ""}`}
                          value={form.output_unit}
                          aria-label="Satuan hasil formula"
                          onChange={(event) => setForm((current) => ({ ...current, output_unit: event.target.value }))}>
                          {compatibleUnitOptions(material?.unit || defaultOutputUnit).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </AkasSelect>
                      </div>
                      {validationErrors.output_quantity || validationErrors.output_unit ? (
                        <div className="invalid-feedback d-block">{validationErrors.output_quantity || validationErrors.output_unit}</div>
                      ) : null}
                    </div>
                    <div className="col-sm-6 col-lg-3">
                      <label className="form-label" htmlFor="formula-process-cost">Biaya proses</label>
                      <div className="input-group">
                        <span className="input-group-text">Rp</span>
                        <NumberInput
                          id="formula-process-cost"
                          type="number"
                          min="0"
                          className={`form-control ${validationErrors.process_cost ? "is-invalid" : ""}`}
                          value={form.process_cost}
                          placeholder="0"
                          onWheel={(event) => event.currentTarget.blur()}
                          onChange={(event) => {
                            setForm((current) => ({ ...current, process_cost: event.target.value }));
                            setValidationErrors((current) => ({ ...current, process_cost: "" }));
                          }}
                        />
                      </div>
                      {validationErrors.process_cost ? <div className="invalid-feedback d-block">{validationErrors.process_cost}</div> : null}
                    </div>
                  </div>

                  <div className="card mt-4">
                    <div className="card-header">
                      <div>
                        <h3 className="card-title">Bahan penyusun</h3>
                        <div className="text-secondary small mt-1 material-formula-section-copy">
                          Takaran berikut menghasilkan jumlah material yang ditetapkan di atas.
                        </div>
                      </div>
                      <div className="card-actions">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }))}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                          Tambah bahan
                        </button>
                      </div>
                    </div>

                    <div className="table-responsive akas-input-table-scroll material-formula-table-wrap" tabIndex="0" aria-label="Daftar bahan penyusun formula">
                      <table className="table table-vcenter card-table material-formula-table">
                      <thead>
                        <tr>
                          <th>Bahan</th>
                          <th>Takaran</th>
                          <th>Satuan</th>
                          <th className="text-end">Estimasi biaya</th>
                          <th aria-label="Aksi"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((item, index) => {
                          const source = selectedMaterial(item.material_id);
                          const option = materialOptions.find((materialOption) => String(materialOption.value) === String(item.material_id));
                          const rowUnitOptions = compatibleUnitOptions(source?.unit || item.unit || "pcs");
                          const usedIds = form.items.map((row, rowIndex) => (rowIndex === index ? "" : String(row.material_id || "")));
                          const baseUnit = getBaseUnitValue(unitOptions, item.unit || source?.unit);
                          const baseQuantity = numberValue(item.quantity) * getUnitFactorToBase(unitOptions, item.unit || source?.unit);
                          const sourceCost = numberValue(source?.price_trend?.current_price || source?.unit_cost);
                          const rowCost = ["gram", "ml"].includes(baseUnit)
                            ? sourceCost * (baseQuantity / 1000)
                            : sourceCost * baseQuantity;
                          const digits = Math.max(1, formatNumberInput(item.quantity, { editing: true }).length);

                          return (
                            <tr key={`formula-item-${index}`}>
                              <td className="material-formula-table__material">
                                <Select
                                  options={materialOptions.filter((materialOption) => !usedIds.includes(String(materialOption.value)))}
                                  value={option || null}
                                  classNamePrefix="formula-select"
                                  className={`form-select p-0 ${itemErrors[index]?.material_id ? "is-invalid" : ""}`.trim()}
                                  styles={formulaSelectStyles}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuPlacement="auto"
                                  menuShouldScrollIntoView={false}
                                  placeholder="Pilih material"
                                  noOptionsMessage={() => "Material tidak tersedia"}
                                  formatOptionLabel={(materialOption) => (
                                    <div className="d-flex align-items-center gap-2">
                                      <UserAvatar
                                        fullName={materialOption.material?.name}
                                        imageUrl={materialOption.material?.image_path || materialOption.material?.image}
                                        image_path={materialOption.material?.image_path || materialOption.material?.image}
                                        size="xs"
                                        justPhotoView={false}
                                      />
                                      <div className="min-w-0">
                                        <div className="text-truncate fw-bold">{materialOption.label}</div>
                                        <div className="text-secondary small">
                                          {materialOption.material?.is_virtual_formula ? "Formula" : "Stok"} · {Number(materialOption.material?.stock || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })} {getBaseUnitValue(unitOptions, materialOption.material?.unit)}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  onChange={(selected) =>
                                    updateItem(index, {
                                      material_id: selected?.value || "",
                                      unit: selected ? getBaseUnitValue(unitOptions, selected.material?.unit) : "",
                                      quantity: "",
                                    })
                                  }
                                />
                                {itemErrors[index]?.material_id ? <div className="invalid-feedback d-block">{itemErrors[index].material_id}</div> : null}
                              </td>
                              <td>
                                <NumberInput
                                  type="number"
                                  min="0.0001"
                                  step="any"
                                  className={`form-control material-formula-qty ${itemErrors[index]?.quantity ? "is-invalid" : ""}`}
                                  style={{ "--formula-digits": digits }}
                                  value={item.quantity}
                                  placeholder="0"
                                  aria-label={`Takaran bahan ${source?.name || index + 1}`}
                                  onWheel={(event) => event.currentTarget.blur()}
                                  onChange={(event) => updateItem(index, { quantity: event.target.value })}
                                />
                                {itemErrors[index]?.quantity ? <div className="invalid-feedback d-block">{itemErrors[index].quantity}</div> : null}
                              </td>
                              <td>
                                <AkasSelect
                                  className={`form-select material-formula-unit ${itemErrors[index]?.unit ? "is-invalid" : ""}`}
                                  value={item.unit}
                                  aria-label={`Satuan bahan ${source?.name || index + 1}`}
                                  disabled={!source}
                                  onChange={(event) => updateItem(index, { unit: event.target.value })}>
                                  <option value="">Satuan</option>
                                  {rowUnitOptions.map((unitOption) => (
                                    <option key={unitOption.value} value={unitOption.value}>{unitOption.label}</option>
                                  ))}
                                </AkasSelect>
                              </td>
                              <td className="text-end fw-bold text-nowrap">{formatIDR(Math.round(rowCost))}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-icon btn-ghost-danger"
                                  aria-label={`Hapus bahan ${source?.name || index + 1}`}
                                  onClick={() => removeItem(index)}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      </table>
                    </div>

                    <div className="card-footer material-formula-summary" aria-live="polite">
                      <div>
                        <span>Estimasi biaya satu formula</span>
                        <strong>{formatIDR(Math.round(estimate.batchCost))}</strong>
                      </div>
                      <div>
                        <span>HPP efektif</span>
                        <strong>{formatIDR(Math.round(estimate.effectiveUnitCost))}/{estimate.priceLabel}</strong>
                      </div>
                      <p>Nilai akhir dihitung ulang oleh server dari harga bahan outlet aktif.</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer material-formula-modal__footer">
              {formulaExists ? (
                <button
                  type="button"
                  className="btn btn-ghost-danger me-auto"
                  data-bs-toggle="modal"
                  data-bs-target="#modal-disable-material-formula"
                  disabled={loading}>
                  Nonaktifkan formula
                </button>
              ) : <span className="me-auto" />}
              <button type="button" className="btn btn-link link-secondary" data-bs-dismiss="modal" disabled={loading}>Batal</button>
              <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSave}>
                {loading ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M5 12l4 4L19 6" />
                  </svg>
                )}
                {loading ? "Menyimpan…" : "Simpan formula"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AlertConfirmModal
        idModal="disable-material-formula"
        type="warning"
        title="Nonaktifkan formula?"
        message={`${material?.name || "Material"} akan kembali menggunakan stok sendiri. Histori transaksi lama tetap tersimpan.`}
        confirmText="Nonaktifkan"
        cancelText="Batal"
        waitForConfirm
        idData={material?.id}
        onConfirm={handleDisable}
      />
    </>
  );
};

export default MaterialFormulaModal;
